/* Public Balance Sheet share page — borrowers can fill out and submit. */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import BalanceSheetCard from './BalanceSheetCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BalanceSheetPublicForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [draftData, setDraftData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/balance-sheet-link/${token}`);
        setInfo(resp.data);
        setDraftData(resp.data.existing_data || {});
        if (resp.data.used) setSubmitted(true);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/balance-sheet-link/${token}`, { data: draftData });
      setSubmitted(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-md text-center" data-testid="bs-public-error">
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-[#081120] px-6 py-5">
          <h1 className="text-xl font-bold text-white" data-testid="bs-public-header">
            {info?.broker_company_name || 'Quidity Direct'}
          </h1>
          <p className="text-sm text-slate-300 mt-1">Balance Sheet</p>
          {info?.business_name && (
            <p className="text-xs text-cyan-300 mt-2 font-semibold uppercase tracking-wide">{info.business_name}</p>
          )}
        </div>

        <div className="p-6 space-y-4">
          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="bs-public-submitted">
              <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-emerald-900">Balance Sheet Submitted</h3>
              <p className="text-sm text-emerald-700 mt-1">Thank you — your balance sheet has been recorded.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Please complete the balance sheet below. Enter the dollar amounts for each line item — leave blank or
                use zero for anything that doesn&apos;t apply. Your draft is automatically saved when you click &quot;Save&quot;.
              </p>
              <BalanceSheetCard
                linkToken={token}
                data={draftData}
                uploadedFiles={info?.uploaded_files || []}
                businessName={info?.business_name}
                onSave={(d) => setDraftData(d)}
              />
              <div className="flex justify-end pt-2 border-t border-slate-200">
                <Button
                  className="bg-cyan-700 hover:bg-cyan-800 text-white"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="bs-public-submit-btn"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="h-4 w-4 mr-2" /> Submit Balance Sheet</>}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetPublicForm;
